import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Paper,
    List,
    ListItem,
    ListItemText,
    ListItemAvatar,
    Avatar,
    Chip,
    Button,
} from '@mui/material';
import {
    Store,
    Directions,
} from '@mui/icons-material';
import MobileContainer from '../global/components/MobileContainer';
import { MAIN_COLOR_RED } from '../assets/styles';
import StoreMap from '../components/StoreMap';
import { useUserLocation } from '../hooks/useUserLocation';
import { storeApi } from '../api';


const MapPage: React.FC = () => {
    const { 
        userPosition, 
        movementHistory, 
        moveToSection,
    } = useUserLocation();

    // Fetch stores from API and use first store for map (backend returns e.g. id 1 "Vasco de Gama")
    const [storeId, setStoreId] = useState<string>('1');
    const [storeName, setStoreName] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;
        storeApi.getStores()
            .then(({ stores }) => {
                if (cancelled || !stores?.length) return;
                const first = stores[0];
                setStoreId(String(first.id));
                setStoreName(first.name);
            })
            .catch(() => {
                if (!cancelled) setStoreId('1');
            });
        return () => { cancelled = true; };
    }, []);


    // Mock store locations
    const stores = [
        {
            id: 1,
            name: 'Continente - Shopping Center',
            address: 'Rua das Flores, 123, Lisboa',
            distance: '0.8 km',
            rating: 4.5,
            open: true,
            hours: '08:00 - 22:00',
            category: 'Supermarket',
        },
        {
            id: 2,
            name: 'Continente - Downtown',
            address: 'Avenida da Liberdade, 456, Lisboa',
            distance: '1.2 km',
            rating: 4.3,
            open: true,
            hours: '07:00 - 23:00',
            category: 'Supermarket',
        },
        {
            id: 3,
            name: 'Continente - Mall',
            address: 'Centro Comercial Colombo, Lisboa',
            distance: '2.1 km',
            rating: 4.7,
            open: false,
            hours: '09:00 - 21:00',
            category: 'Supermarket',
        },
    ];

    return (
        <MobileContainer>
            <Box sx={{ mb: 2 }}>
                <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ fontSize: '1.4rem' }}>
                    Store Map
                    {storeName && (
                        <Typography component="span" variant="h5" fontWeight="normal" color="text.secondary" sx={{ ml: 1, fontSize: '1.1rem' }}>
                            – {storeName}
                        </Typography>
                    )}
                </Typography>
            </Box>

            {/* Store Map - use image-based map (store-map.png via StoreMap component) */}
            <StoreMap
                storeId={storeId}
                userPosition={userPosition}
                movementHistory={movementHistory}
                onSectionClick={moveToSection}
            />

            {/* Store list */}
            <Box>
                <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ fontSize: '1.2rem', mb: 2 }}>
                    Nearby Stores
                </Typography>
                
                <List sx={{ p: 0 }}>
                    {stores.map((store) => (
                        <Paper key={store.id} sx={{ mb: 1.5, overflow: 'hidden', borderRadius: 2 }}>
                            <ListItem sx={{ py: 1.5, px: 2 }}>
                                <ListItemAvatar>
                                    <Avatar sx={{ bgcolor: MAIN_COLOR_RED, width: 40, height: 40 }}>
                                        <Store sx={{ fontSize: 20 }} />
                                    </Avatar>
                                </ListItemAvatar>
                                <ListItemText
                                    primary={
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                                            <Typography variant="subtitle1" fontWeight="bold" sx={{ fontSize: '1rem' }}>
                                                {store.name}
                                            </Typography>
                                            <Chip
                                                label={store.open ? 'Open' : 'Closed'}
                                                size="small"
                                                color={store.open ? 'success' : 'error'}
                                                sx={{ fontSize: '0.65rem', height: 20 }}
                                            />
                                        </Box>
                                    }
                                    secondary={
                                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem', lineHeight: 1.3 }}>
                                            {store.address}
                                        </Typography>
                                    }
                                />
                                <Box sx={{ mt: 1, width: '100%' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1, flexWrap: 'wrap' }}>
                                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                            📍 {store.distance}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                            ⭐ {store.rating}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                            🕒 {store.hours}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            disabled
                                            startIcon={<Directions />}
                                            sx={{ 
                                                borderColor: MAIN_COLOR_RED, 
                                                color: MAIN_COLOR_RED,
                                                fontSize: '0.75rem',
                                                py: 0.5,
                                                px: 1.5,
                                                '&:hover': {
                                                    borderColor: MAIN_COLOR_RED,
                                                    backgroundColor: 'rgba(220, 38, 38, 0.04)',
                                                }
                                            }}
                                        >
                                            Directions
                                        </Button>
                                        <Button
                                            size="small"
                                            variant="contained"
                                            disabled
                                            sx={{ 
                                                backgroundColor: MAIN_COLOR_RED,
                                                fontSize: '0.75rem',
                                                py: 0.5,
                                                px: 1.5
                                            }}
                                        >
                                            Shop Here
                                        </Button>
                                    </Box>
                                </Box>
                            </ListItem>
                        </Paper>
                    ))}
                </List>
            </Box>
        </MobileContainer>
    );
};

export default MapPage;
